import { useCallback, useEffect, useRef, useState } from "react";import { useSearchParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { ConsultationStage, ActivityFeed } from "@/components/ConsultationStage";
import { apiErr } from "@/lib/api";
import {
  ShieldCheck, KeyRound, AlertTriangle, Camera, Mic, MicOff, CameraOff,
  ArrowRight, Loader2, DoorClosed,
} from "lucide-react";

/**
 * Public join page for an invited visitor — interpreter, relative,
 * companion or second clinician. No account, often a borrowed phone.
 * URL shape: /join?token=pj_xxxxx
 *
 * Three steps: code → device check (advisory) → consultation. The
 * exchange returns ONLY the video room — never any clinical data, and
 * never the guest's or doctor's identity.
 */
const ROLE_COPY = {
  interpreter: "interpreter",
  caregiver: "carer",
  family_member: "family member",
  additional_doctor: "second clinician",
  specialist: "specialist",
  translator: "interpreter",
  guardian: "carer",
  clinician: "second clinician",
};

export default function ParticipantJoin() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const [passcode, setPasscode] = useState("");
  const [displayName, setDisplayName] = useState("");
  const [joining, setJoining] = useState(false);
  const [session, setSession] = useState(null);
  const [phase, setPhase] = useState("code");   // code | check | waiting | call
  const [error, setError] = useState(null);

  const backendUrl = process.env.REACT_APP_BACKEND_URL;
  const client = axios.create({ baseURL: `${backendUrl}/api/v1`, timeout: 15000 });

  const submit = async (e) => {
    e.preventDefault();
    if (passcode.length !== 6) { toast.error("The code is 6 digits"); return; }
    setJoining(true);
    setError(null);
    try {
      const { data } = await client.post("/participants/join", {
        token, passcode, display_name: displayName || undefined,
      });
      setSession(data);
      setPhase("check");
    } catch (err) {
      setError(apiErr(err, "We couldn't connect you just now. Please try again in a moment."));
    } finally {
      setJoining(false);
    }
  };

  /** Re-knock: the door may have been opened (or closed) since last time. */
  const refreshDoor = useCallback(async () => {
    const { data } = await client.post("/participants/join", {
      token, passcode, display_name: displayName || undefined,
    });
    setSession(data);
    return data;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, passcode, displayName]);

  // While waiting outside — or in the call, in case we're put back outside.
  useEffect(() => {
    if (phase !== "waiting" && phase !== "call") return undefined;
    const t = setInterval(async () => {
      try {
        const data = await refreshDoor();
        if (phase === "waiting" && data.door_state === "admitted") {
          setPhase("call");
          client.post("/participants/joined", { token, passcode }).catch(() => {});
        } else if (phase === "call" && data.door_state !== "admitted") {
          setPhase("waiting");
          toast("You've been asked to wait outside for a moment");
        }
      } catch { /* keep waiting quietly */ }
    }, 4000);
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [phase, refreshDoor]);

  const enterCall = async () => {
    try {
      const data = await refreshDoor();
      if (data.door_state === "admitted") {
        setPhase("call");
        client.post("/participants/joined", { token, passcode }).catch(() => {});
      } else {
        setPhase("waiting");
      }
    } catch {
      setPhase("waiting");
    }
  };

  const leave = () => {
    client.post("/participants/left", { token, passcode }).catch(() => {});
    setPhase("code");
    setSession(null);
    setPasscode("");
    toast.success("You have left the consultation");
  };

  /**
   * The call surface disconnects for two very different reasons: the visitor
   * hung up, or they were put back outside. Only the first is leaving.
   */
  const handleStageLeave = async () => {
    try {
      const data = await refreshDoor();
      if (data.door_state !== "admitted") {
        setPhase("waiting");
        toast("You've been asked to wait outside for a moment");
        return;
      }
    } catch { /* fall through to a normal leave */ }
    leave();
  };

  // ------------------------ IN THE CONSULTATION ------------------------
  if (session && phase === "call") {
    const roleLabel = ROLE_COPY[session.invitee_role] || session.invitee_role;
    const activityItems = [{
      id: `granted-${session.invite_id || session.livekit_room}`,
      at: Date.now(),
      kind: "system",
      title: "Video-only access granted",
      body: "You do not have access to the clinical record. Do not record, screenshot, or share the room.",
    }];
    if (session.expires_at) {
      activityItems.push({
        id: `expiry-${session.invite_id || session.livekit_room}`,
        at: session.expires_at,
        kind: "status",
        title: "Session access will expire",
        body: `Access ends ${new Date(session.expires_at).toLocaleTimeString()}.`,
      });
    }
    return (
      <div data-testid="participant-video" className="min-h-screen">
        <ConsultationStage
          roomName={session.livekit_room}
          displayName={session.invitee_display_name}
          jwt={session.livekit_token || session.token}
          domain={session.livekit_url || session.join_url}
          providerId={session.provider_id || "livekit"}
          hipaaMode={!!session.hipaa_mode}
          autoJoin
          onLeave={handleStageLeave}
          eventCount={activityItems.length}
          peerName={session.invitee_display_name}
          peerSubtitle={roleLabel.charAt(0).toUpperCase() + roleLabel.slice(1)}
          backTo="/"
          activity={<ActivityFeed items={activityItems} />}
        />
      </div>
    );
  }

  // ------------------------ WAITING AT THE DOOR ------------------------
  if (session && phase === "waiting") {
    return (
      <div className="lux-scope min-h-screen" data-testid="participant-waiting-room">
        <div className="max-w-md mx-auto px-5 pt-20 pb-16 text-center">
          <div className="h-14 w-14 mx-auto rounded-2xl flex items-center justify-center"
               style={{ background: "rgba(214,158,46,0.16)" }}>
            <DoorClosed className="h-6 w-6" style={{ color: "var(--lux-gold)" }} />
          </div>
          <div className="lux-eyebrow mt-5">Waiting to be let in</div>
          <h1 className="lux-serif text-4xl mt-2 leading-tight">You&apos;re at the door</h1>
          <p className="mt-3 text-sm leading-relaxed" style={{ color: "var(--lux-ink-soft)" }}>
            {session.waiting_message
              || "They've been told you're here. You'll be let in shortly."}
          </p>
          <div className="mt-6 inline-flex items-center gap-2 text-xs" style={{ color: "var(--lux-ink-soft)" }}>
            <Loader2 className="h-3.5 w-3.5 animate-spin" /> Checking every few seconds
          </div>
          <div className="mt-8">
            <button onClick={() => { setPhase("code"); setSession(null); }}
                    className="text-xs underline underline-offset-2"
                    style={{ color: "var(--lux-ink-soft)" }}
                    data-testid="participant-waiting-cancel">
              Leave the queue
            </button>
          </div>
        </div>
      </div>
    );
  }

  // ------------------------ DEVICE CHECK ------------------------
  if (session && phase === "check") {
    return (
      <DeviceCheck
        session={session}
        onEnter={enterCall}
        onCancel={() => { setSession(null); setPhase("code"); setPasscode(""); }}
      />
    );
  }

  // ------------------------ CODE ENTRY ------------------------
  return (
    <div className="lux-scope min-h-screen" data-testid="participant-join-page">
      <div className="max-w-md mx-auto px-5 pt-14 pb-16" data-testid="participant-join-form">
        <div className="flex items-center gap-2">
          <ShieldCheck className="h-4 w-4" style={{ color: "var(--lux-gold)" }} />
          <div className="lux-eyebrow">AllRound Health Concierge</div>
        </div>
        <h1 className="lux-serif text-4xl mt-3 leading-tight">You&apos;ve been invited to a consultation</h1>
        <p className="mt-3 text-sm leading-relaxed" style={{ color: "var(--lux-ink-soft)" }}>
          Enter the 6-digit code you were given. The person who invited you has it —
          it is never sent with this link.
        </p>

        <form onSubmit={submit} className="mt-6 lux-card p-6 space-y-5">
          {!token ? (
            <div className="rounded-xl border p-3 text-sm flex items-start gap-2"
                 style={{ borderColor: "rgba(182,80,58,0.35)", background: "rgba(182,80,58,0.07)" }}
                 data-testid="participant-join-error">
              <AlertTriangle className="h-4 w-4 shrink-0 mt-0.5" style={{ color: "#B6503A" }} />
              <span>This link is incomplete. Ask the person who invited you to send it again.</span>
            </div>
          ) : (
          <>
          <label className="block">
            <div className="lux-eyebrow mb-2">Your name on the call</div>
            <input
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
              className="lux-input"
              placeholder="Leave blank to use the invited name"
              data-testid="participant-name-input"
              maxLength={80}
            />
          </label>

          <label className="block">
            <div className="lux-eyebrow flex items-center gap-1.5 mb-2">
              <KeyRound className="h-3.5 w-3.5" /> 6-digit code
            </div>
            <input
              value={passcode}
              onChange={(e) => setPasscode(e.target.value.replace(/[^0-9]/g, "").slice(0, 6))}
              inputMode="numeric"
              className="lux-input text-2xl tracking-[0.35em] text-center font-mono h-14"
              placeholder="123456"
              data-testid="participant-passcode-input"
              autoFocus
            />
          </label>

          <button
            type="submit"
            disabled={joining || passcode.length !== 6}
            className="lux-btn w-full"
            data-testid="participant-join-btn"
          >
            {joining ? "Checking…" : (<>Continue <ArrowRight className="h-4 w-4" /></>)}
          </button>

          {error && (
            <div className="rounded-xl border p-3 text-sm flex items-start gap-2"
                 style={{ borderColor: "rgba(182,80,58,0.35)", background: "rgba(182,80,58,0.07)" }}
                 data-testid="participant-join-error">
              <AlertTriangle className="h-4 w-4 shrink-0 mt-0.5" style={{ color: "#B6503A" }} />
              <span>{error}</span>
            </div>
          )}
          </>
          )}

          <p className="text-[11px] leading-relaxed pt-3 border-t"
             style={{ color: "var(--lux-ink-soft)", borderColor: "rgba(32,29,23,0.08)" }}>
            You will see and hear the call. You will never see medical notes,
            prescriptions or results. Please don&apos;t record or share what you see.
          </p>
        </form>
      </div>
    </div>
  );
}

/** Advisory camera + microphone check with a self-preview. */
function DeviceCheck({ session, onEnter, onCancel }) {
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const [state, setState] = useState("checking");
  const [devices, setDevices] = useState({ camera: false, mic: false });
  const roleLabel = ROLE_COPY[session.invitee_role] || session.invitee_role || "participant";

  const stop = useCallback(() => {
    streamRef.current?.getTracks().forEach((t) => t.stop());
    streamRef.current = null;
  }, []);

  const probe = useCallback(async () => {
    setState("checking");
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ video: true, audio: true });
      streamRef.current = stream;
      if (videoRef.current) videoRef.current.srcObject = stream;
      setDevices({
        camera: stream.getVideoTracks().length > 0,
        mic: stream.getAudioTracks().length > 0,
      });
      setState("ready");
    } catch {
      try {
        const audioOnly = await navigator.mediaDevices.getUserMedia({ audio: true });
        streamRef.current = audioOnly;
        setDevices({ camera: false, mic: audioOnly.getAudioTracks().length > 0 });
        setState("audio-only");
      } catch {
        setDevices({ camera: false, mic: false });
        setState("blocked");
      }
    }
  }, []);

  useEffect(() => {
    probe();
    return stop;
  }, [probe, stop]);

  const enter = () => { stop(); onEnter(); };

  return (
    <div className="lux-scope min-h-screen" data-testid="participant-device-check">
      <div className="max-w-md mx-auto px-5 pt-14 pb-16">
        <div className="lux-eyebrow">Joining as {roleLabel}</div>
        <h1 className="lux-serif text-4xl mt-2 leading-tight">Ready when you are</h1>
        <p className="mt-3 text-sm leading-relaxed" style={{ color: "var(--lux-ink-soft)" }}>
          You&apos;ll appear as <strong>{session.invitee_display_name}</strong>. A quick check
          of your camera and microphone first.
        </p>

        <div className="mt-6 lux-card p-6 space-y-5">
          <div className="rounded-2xl overflow-hidden bg-[#23342A] aspect-video flex items-center justify-center relative">
            <video
              ref={videoRef} autoPlay playsInline muted
              className="h-full w-full object-cover"
              data-testid="participant-self-preview"
            />
            {state !== "ready" && (
              <div className="absolute inset-0 flex flex-col items-center justify-center gap-2 text-cream text-sm">
                {state === "checking"
                  ? <><Loader2 className="h-6 w-6 animate-spin" /> Checking your devices…</>
                  : <><CameraOff className="h-6 w-6 opacity-70" /> No camera preview</>}
              </div>
            )}
          </div>

          <div className="flex gap-2" data-testid="participant-device-state">
            <Chip ok={devices.camera} okIcon={<Camera className="h-3.5 w-3.5" />}
                  offIcon={<CameraOff className="h-3.5 w-3.5" />}
                  label={devices.camera ? "Camera ready" : "No camera"} />
            <Chip ok={devices.mic} okIcon={<Mic className="h-3.5 w-3.5" />}
                  offIcon={<MicOff className="h-3.5 w-3.5" />}
                  label={devices.mic ? "Microphone ready" : "No microphone"} />
          </div>

          {state === "audio-only" && (
            <p className="text-xs" style={{ color: "var(--lux-ink-soft)" }}>
              Your camera isn&apos;t available, so you&apos;ll join with sound only. That&apos;s fine —
              you can still hear and speak.
            </p>
          )}
          {state === "blocked" && (
            <p className="text-xs" style={{ color: "#B6503A" }} data-testid="participant-devices-blocked">
              This device hasn&apos;t allowed camera or microphone access. You can still join
              and listen, or allow access in your browser settings and check again.
            </p>
          )}

          {session.video_available === false && (
            <p className="text-xs" style={{ color: "#B6503A" }} data-testid="participant-video-unavailable">
              Video is temporarily unavailable on our side. Please ask the person who
              invited you to continue by message instead.
            </p>
          )}

          <button onClick={enter} className="lux-btn w-full" data-testid="participant-enter-btn">
            {session.door_state === "admitted"
              ? (<>Enter the consultation <ArrowRight className="h-4 w-4" /></>)
              : (<>Let them know I&apos;m ready <ArrowRight className="h-4 w-4" /></>)}
          </button>
          {session.door_state !== "admitted" && (
            <p className="text-xs text-center" style={{ color: "var(--lux-ink-soft)" }}>
              Someone inside has to let you in — you&apos;ll wait a moment at the door.
            </p>
          )}
          <div className="flex items-center justify-between">
            <button onClick={probe} className="text-xs underline underline-offset-2"
                    style={{ color: "var(--lux-ink-soft)" }} data-testid="participant-recheck-btn">
              Check again
            </button>
            <button onClick={() => { stop(); onCancel(); }} className="text-xs underline underline-offset-2"
                    style={{ color: "var(--lux-ink-soft)" }} data-testid="participant-cancel-btn">
              Not now
            </button>
          </div>

          <p className="text-[11px] leading-relaxed pt-3 border-t"
             style={{ color: "var(--lux-ink-soft)", borderColor: "rgba(32,29,23,0.08)" }}>
            You will see and hear the call. You will never see medical notes,
            prescriptions or results.
          </p>
        </div>
      </div>
    </div>
  );
}

function Chip({ ok, label, okIcon, offIcon }) {
  return (
    <span
      className="inline-flex items-center gap-1.5 text-[11px] font-semibold px-2.5 py-1.5 rounded-full border"
      style={ok
        ? { color: "#1f5134", background: "rgba(31,81,52,0.08)", borderColor: "rgba(31,81,52,0.25)" }
        : { color: "var(--lux-ink-soft)", background: "rgba(32,29,23,0.04)", borderColor: "rgba(32,29,23,0.14)" }}
    >
      {ok ? okIcon : offIcon} {label}
    </span>
  );
}
